import { compareAsc } from 'date-fns'
import { create } from 'zustand'

import {
  Journey,
  JourneyTripOption,
  JourneyTripOptionTableData
} from '../types/models/product/journey'
import { ProductDate } from '../types/models/product/product-date'

type ProductStore = {
  productDates: ProductDate[]
  setProductDates: (productDates: ProductDate[]) => void
  addProductDate: (productDate: ProductDate) => void
  removeProductDate: (productDate: ProductDate) => void
  lockProductDate: (id: number) => void
  unlockProductDate: (id: number) => void
  updateProductDate: (id: number, productDate: ProductDate) => void
  addJourney: (productDateId: number, journey: Journey) => void
  removeJourney: (journeyId: number) => void
  updateJourney: (journeyId: number, journey: Journey) => void
  publishJourney: (journeyId: number) => void
  unpublishJourney: (journeyId: number) => void
  addJourneyPlaces: (journeyId: number, places: JourneyTripOption[]) => void
  removeJourneyPlacesGroup: (journeyId: number, placeId: number, mainTripId: number) => void
  publishJourneyPlacesGroup: (journeyId: number, placeId: number, mainTripId: number) => void
  unpublishJourneyPlacesGroup: (journeyId: number, placeId: number, mainTripId: number) => void
  publishJourneyPlace: (journeyId: number, journeyPlaceId: number) => void
  unpublishJourneyPlace: (journeyId: number, journeyPlaceId: number) => void
  groupJourneyPlaces: (journeyId: number) => JourneyTripOptionTableData[]
}

export const useProductStore = create<ProductStore>((set) => ({
  productDates: [],
  setProductDates: (productDates) => set((state) => ({ ...state, productDates })),

  addProductDate: (productDate) =>
    set((state) => ({
      ...state,
      productDates: [...state.productDates, productDate].sort((a, b) =>
        compareAsc(new Date(a.departureDate), new Date(b.departureDate))
      )
    })),

  removeProductDate: (productDate) =>
    set((state) => ({
      ...state,
      productDates: state.productDates.filter((date) => date.id !== productDate.id)
    })),

  updateProductDate: (id, productDate) =>
    set((state) => ({
      ...state,
      productDates: state.productDates.map((date) => (date.id === id ? productDate : date))
    })),

  addJourney: (productDateId, journey) =>
    set((state) => ({
      ...state,
      productDates: state.productDates.map((date) =>
        date.id === productDateId ? { ...date, journeys: [...date.journeys, journey] } : date
      )
    })),

  removeJourney: (journeyId) =>
    set((state) => ({
      ...state,
      productDates: state.productDates.map((date) => ({
        ...date,
        journeys: date.journeys.filter((j) => j.id !== journeyId)
      }))
    })),

  updateJourney: (journeyId, journey) =>
    set((state) => ({
      ...state,
      productDates: state.productDates.map((date) => ({
        ...date,
        journeys: date.journeys.map((j) => (j.id === journeyId ? journey : j))
      }))
    })),

  lockProductDate: (id) =>
    set((state) => ({
      ...state,
      productDates: state.productDates.map((date) =>
        date.id === id ? { ...date, locked: true } : date
      )
    })),

  unlockProductDate: (id) =>
    set((state) => ({
      ...state,
      productDates: state.productDates.map((date) =>
        date.id === id ? { ...date, locked: false } : date
      )
    })),

  publishJourney: (journeyId) =>
    set((state) => ({
      ...state,
      productDates: state.productDates.map((date) => ({
        ...date,
        journeys: date.journeys.map((j) =>
          j.id === journeyId ? { ...j, published: true, publishedUpdatedAt: new Date() } : j
        )
      }))
    })),

  unpublishJourney: (journeyId) =>
    set((state) => ({
      ...state,
      productDates: state.productDates.map((date) => ({
        ...date,
        journeys: date.journeys.map((j) =>
          j.id === journeyId ? { ...j, published: false, publishedUpdatedAt: new Date() } : j
        )
      }))
    })),

  addJourneyPlaces: (journeyId, places) =>
    set((state) => ({
      ...state,
      productDates: state.productDates.map((date) => ({
        ...date,
        journeys: date.journeys.map((j) => (j.id === journeyId ? { ...j, places } : j))
      }))
    })),

  removeJourneyPlacesGroup: (journeyId, placeId, mainTripId) =>
    set((state) => ({
      ...state,
      productDates: state.productDates.map((date) => ({
        ...date,
        journeys: date.journeys.map((j) =>
          j.id === journeyId
            ? {
                ...j,
                places: j.places.filter(
                  (p) => p.placeId !== placeId || p.mainTripId !== mainTripId
                )
              }
            : j
        )
      }))
    })),

  publishJourneyPlacesGroup: (journeyId, placeId, mainTripId) =>
    set((state) => ({
      ...state,
      productDates: state.productDates.map((date) => ({
        ...date,
        journeys: date.journeys.map((j) =>
          j.id === journeyId
            ? {
                ...j,
                places: j.places.map((p) =>
                  p.placeId === placeId && p.mainTripId === mainTripId
                    ? { ...p, published: true }
                    : p
                )
              }
            : j
        )
      }))
    })),
  unpublishJourneyPlacesGroup: (journeyId, placeId, mainTripId) =>
    set((state) => ({
      ...state,
      productDates: state.productDates.map((date) => ({
        ...date,
        journeys: date.journeys.map((j) =>
          j.id === journeyId
            ? {
                ...j,
                places: j.places.map((p) =>
                  p.placeId === placeId && p.mainTripId === mainTripId
                    ? { ...p, published: false }
                    : p
                )
              }
            : j
        )
      }))
    })),

  // publish the journey place and the group of places it belongs to
  publishJourneyPlace: (journeyId, journeyPlaceId) =>
    set((state) => {
      const journeyTripOption = state.productDates
        .flatMap((date) => date.journeys)
        .find((j) => j.id === journeyId)
        ?.places.find((p) => p.id === journeyPlaceId)

      return {
        ...state,
        productDates: state.productDates.map((date) => ({
          ...date,
          journeys: date.journeys.map((j) =>
            j.id === journeyId
              ? {
                  ...j,
                  places: j.places.map((p) =>
                    // Check if it's the place being published or part of the group
                    p.id === journeyPlaceId ||
                    (journeyTripOption &&
                      p.mainTrip.id === journeyTripOption.mainTrip.id &&
                      p.place.id === journeyTripOption.place.id &&
                      !p.secondaryTripId)
                      ? { ...p, published: true }
                      : p
                  )
                }
              : j
          )
        }))
      }
    }),

  unpublishJourneyPlace: (journeyId, journeyPlaceId) =>
    set((state) => ({
      ...state,
      productDates: state.productDates.map((date) => ({
        ...date,
        journeys: date.journeys.map((j) =>
          j.id === journeyId
            ? {
                ...j,
                places: j.places.map((p) =>
                  p.id === journeyPlaceId ? { ...p, published: false } : p
                )
              }
            : j
        )
      }))
    })),
  groupJourneyPlaces: (journeyId) => {
    const state = useProductStore.getState()
    const journey = state.productDates
      .flatMap((date) => date.journeys)
      .find((j) => j.id === journeyId)

    if (!journey) return []

    const places = journey?.places

    const groupedPlaces: JourneyTripOptionTableData[] = []

    places.forEach((place) => {
      const existingGroup = groupedPlaces.find(
        (group) => group.mainTripId === place.mainTrip.id && group.placeId === place.place.id
      )

      if (existingGroup) {
        if (place.secondaryTrip) existingGroup.secondaryTrips.push(place.secondaryTrip)
      } else {
        // The record is published if at least one of its trip options is published
        groupedPlaces.push({
          ...place,
          published:
            places
              .filter((p) => p.mainTrip.id === place.mainTrip.id && p.place.id === place.place.id)
              ?.some((p) => p.published) || false,
          secondaryTrips: place.secondaryTrip ? [place.secondaryTrip] : []
        })
      }
    })

    return groupedPlaces
  }
}))
